import { MA_TYPE } from "../indicatorTypes";

// Warm-up as in the built-in studies of TradingView (Std.sma / ema / rma / wma
// of charting_library): no value until the window is full, EMA and SMMA start
// from the SMA of the first window. A missing value (NaN) in the window makes
// the average NaN, so averages of values that start later (MACD) start later too.

const windowSum = (read, index, length) => {
  if (index - length + 1 < 0) return NaN;

  let sum = 0;

  for (let position = index - length + 1; position <= index; position += 1) sum += read(position);

  return sum;
};

/** Simple: the mean of the last `period` values. */
export const smaAt = (read, index, period) => windowSum(read, index, period) / period;

/**
 * Linear weighted: the newest value weighs `period`, the oldest 1. As TV
 * Std.wma it also reads the value before the window (with weight 0), so
 * the first value comes one bar later than the window is full.
 */
export const wmaAt = (read, index, period) => {
  const first = index - period + 1;

  if (first < 1 || !Number.isFinite(read(first - 1))) return NaN;

  let sum = 0;

  for (let weight = 1; weight <= period; weight += 1) sum += read(first + weight - 1) * weight;

  return sum / ((period * (period + 1)) / 2);
};

// The first value is the SMA of the first full window, then each value moves
// towards the new one by `alpha` (TV Std.ema and Std.smma / rma, MetaTrader SMMA)
const recursiveAt = (read, index, period, previous, alpha) => {
  if (!Number.isFinite(previous)) return smaAt(read, index, period);

  return previous + alpha * (read(index) - previous);
};

/** Exponential, alpha = 2 / (period + 1). */
export const emaAt = (read, index, period, previous) =>
  recursiveAt(read, index, period, previous, 2 / (period + 1));

/** Smoothed (Wilder, TV Std.rma / smma, MetaTrader SMMA), alpha = 1 / period. */
export const smmaAt = (read, index, period, previous) =>
  recursiveAt(read, index, period, previous, 1 / period);

/**
 * Triangular (not in TradingView, the classic formula): the SMA of SMAs,
 * ceil((period + 1) / 2) values each, taken floor((period + 1) / 2) times,
 * so the middle of the window weighs most.
 * The inner sums slide, so a value costs about `period` additions.
 */
export const tmaAt = (read, index, period) => {
  const inner = Math.ceil((period + 1) / 2);
  const outer = Math.floor((period + 1) / 2);
  const first = index - inner - outer + 2;

  if (first < 0) return NaN;

  let sum = windowSum(read, first + inner - 1, inner);
  let total = sum;

  for (let end = first + inner; end <= index; end += 1) {
    sum += read(end) - read(end - inner);
    total += sum;
  }

  return total / (inner * outer);
};

const AVERAGES = {
  [MA_TYPE.SMA]: smaAt,
  [MA_TYPE.WMA]: wmaAt,
  [MA_TYPE.EMA]: emaAt,
  [MA_TYPE.SMMA]: smmaAt,
  [MA_TYPE.TMA]: tmaAt,
};

/**
 * One value of a moving average.
 *
 * @param {string} type - MA_TYPE value; SMA when unknown
 * @param {(index: number) => number} read - values to average (priceSources.js)
 * @param {number} index
 * @param {number} period
 * @param {number} previous - the average at index - 1 (EMA, SMMA); NaN or undefined when there is none
 * @returns {number} NaN while the window is not full
 */
export const movingAverageAt = (type, read, index, period, previous) =>
  (AVERAGES[type] || smaAt)(read, index, period, previous);

/**
 * Fills `target` with a moving average from `fromIndex` to `toIndex`
 * (exclusive); values before `fromIndex` stay and seed EMA / SMMA.
 *
 * @param {object} params
 * @param {string} params.type - MA_TYPE value
 * @param {(index: number) => number} params.read
 * @param {number[]} params.target
 * @param {number} params.period
 * @param {number} params.fromIndex
 * @param {number} params.toIndex
 */
export const fillMovingAverage = ({ type, read, target, period, fromIndex, toIndex }) => {
  for (let index = fromIndex; index < toIndex; index += 1) {
    target[index] = movingAverageAt(type, read, index, period, target[index - 1]);
  }
};
